// src/components/museumGuide/MuseumOptionsCard.tsx
//
// Renders a [MUSEUM_OPTIONS] block as tappable museum rows. Used
// during the awaiting_museum state of a museum_guide conversation,
// when the AI offers the museums near the user's GPS position and
// they pick the one they're at (or heading to).
//
// On tap, fires onSelect(museumName) which the parent wires to the
// follow-up-click pipeline — i.e. it sends a synthetic user message
// ("I'm at the Rijksmuseum.") that the AI then responds to. The
// AI's response includes the CONVERSATION_STATE marker capturing
// the museum and advancing the state machine to awaiting_submode.
//
// Same synthetic-message rationale as TourThemesCard:
//   1. The user's choice stays legible in chat history.
//   2. The AI can handle ambiguity (two museums with similar
//      names, the user typing a museum not on the list).
//   3. Reuses existing plumbing.

import React from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Stack,
} from '@mui/material';
import {
  AccountBalanceOutlined,
  NearMeOutlined,
} from '@mui/icons-material';
import { useT } from '../../i18n/useT';
import type { MuseumOptionItem } from '../../utils/interactiveContent';

interface Props {
  items: MuseumOptionItem[];
  /** Fired when the user taps a museum. The parent should treat
   *  this like a typed user message — send it to the AI and let
   *  the state machine play out. */
  onSelect: (museumName: string) => void;
}

const formatDistance = (m: number | undefined): string | null => {
  if (m === undefined) return null;
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
};

export const MuseumOptionsCard: React.FC<Props> = ({ items, onSelect }) => {
  const t = useT();

  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Section heading — same structured-choice affordance as the
          walking-tour theme picker. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 0.5,
          mb: 0.25,
        }}
      >
        <AccountBalanceOutlined
          sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.7 }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            opacity: 0.7,
          }}
        >
          {t('Which museum?')}
        </Typography>
      </Box>

      {items.map((museum, idx) => (
        <MuseumRow
          key={`${museum.name}-${idx}`}
          museum={museum}
          onSelect={() => onSelect(museum.name)}
        />
      ))}
    </Box>
  );
};

interface MuseumRowProps {
  museum: MuseumOptionItem;
  onSelect: () => void;
}

const MuseumRow: React.FC<MuseumRowProps> = ({ museum, onSelect }) => {
  const distance = formatDistance(museum.distance_m);

  return (
    <ButtonBase
      onClick={onSelect}
      focusRipple
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: 1.75,
        py: 1.25,
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        {museum.glyph && (
          <Typography
            component="span"
            sx={{ fontSize: '1.25rem', lineHeight: 1, flexShrink: 0 }}
          >
            {museum.glyph}
          </Typography>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {museum.name}
          </Typography>
          {museum.vicinity && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block' }}
            >
              {museum.vicinity}
            </Typography>
          )}
        </Box>
        {distance && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ flexShrink: 0 }}
          >
            <NearMeOutlined
              sx={{ fontSize: 13, color: 'text.secondary', opacity: 0.8 }}
            />
            <Typography variant="caption" color="text.secondary">
              {distance}
            </Typography>
          </Stack>
        )}
      </Stack>
    </ButtonBase>
  );
};
